import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignaturePad from "../../components/SignaturePad";
import { driverApi } from "../../services/driverApi";

const CHECKLIST = [
  { key: "independentContractorStatus", label: "Independent Contractor Status", section: "§3" },
  { key: "paymentTermsLeaseStructure", label: "Payment Terms and Weekly Lease Structure", section: "§6–7" },
  { key: "insuranceAirportAccess", label: "Insurance and Airport Access Conditions", section: "§10" },
  { key: "vehicleMaintenance", label: "Vehicle Maintenance Responsibilities", section: "§5" },
  { key: "accidentReporting", label: "Accident and Incident Reporting", section: "§11" },
  { key: "safetyRules", label: "Safety Rules and Hours of Service", section: "§12" },
  { key: "codeOfConduct", label: "Code of Conduct", section: "§13" },
  { key: "lostAndFound", label: "Lost and Found", section: "§16" },
  { key: "noSmokingCleanVehicle", label: "No Smoking and Clean Vehicle Policy", section: "§17" },
  { key: "voucherProcessingFees", label: "Voucher, Account, and Processing Fee Rules", section: "§15" },
  { key: "suspensionTermination", label: "Suspension and Termination Policy", section: "§21" },
  { key: "companyRightToUpdate", label: "Company Right to Update Rates, Fees, and Policies", section: "§8" },
];

const STEPS = ["Review Your Info", "Policy Acknowledgment", "Sign Agreement"];

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-gray-900 font-medium">{value || <span className="text-gray-300 italic">Not provided</span>}</p>
    </div>
  );
}

export default function IcaForm({ driver }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    driverApi.getSettings().then((d) => setCompanyName(d.settings?.companyName || d.settings?.companyTradeName || "")).catch(() => {});
  }, []);
  const defaultInitials = `${driver?.firstName?.[0] || ""}${driver?.lastName?.[0] || ""}`;

  const [step, setStep] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [sameAsResidential, setSameAsResidential] = useState(true);
  const [mailing, setMailing] = useState({ street: "", city: "", state: "", zip: "" });
  const [checklistInitials, setChecklistInitials] = useState(
    Object.fromEntries(CHECKLIST.map((c) => [c.key, defaultInitials]))
  );
  const [signerName, setSignerName] = useState(`${driver?.firstName || ""} ${driver?.lastName || ""}`.trim());
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  const addr = driver?.address || {};
  const residentialLine = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  const allInitialsFilled = CHECKLIST.every((c) => checklistInitials[c.key]?.trim());

  async function handleSign({ signatureType, signatureData }) {
    if (!signerName.trim()) { setError("Please enter your full legal name."); return; }
    setSigning(true);
    setError("");
    try {
      await driverApi.signDocument({
        documentId: "independent_contractor_agreement",
        signerName,
        signatureType,
        signatureData,
        formData: {
          effectiveDate,
          sameAsResidential,
          mailingAddress: sameAsResidential ? null : mailing,
          checklistInitials,
        },
      });
      navigate("/enrollme/driver/sign");
    } catch (err) {
      setError(err.message);
      setSigning(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto pb-12">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => step === 0 ? navigate("/enrollme/driver/sign") : setStep(step - 1)}
          className="text-sm text-gray-400 hover:text-gray-600 mb-3 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          {step === 0 ? "Back to Documents" : "Back"}
        </button>
        <h1 className="text-xl font-bold text-gray-900">Independent Contractor Agreement</h1>
        <p className="text-sm text-gray-500 mt-0.5">{companyName}</p>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 ${i === step ? "text-blue-600" : i < step ? "text-green-600" : "text-gray-300"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i === step ? "border-blue-600 bg-blue-50 text-blue-700" : i < step ? "border-green-500 bg-green-500 text-white" : "border-gray-200 bg-white text-gray-400"}`}>
                  {i < step ? "✓" : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-green-400" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-gray-400">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}

      {/* ── Step 0: Review Your Info ─────────────────────────────────────────── */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Please review the information below. It will appear verbatim in the signed agreement. If anything is wrong, update your profile first, then return here.
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">Driver Information</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full Legal Name" value={`${driver?.firstName || ""} ${driver?.lastName || ""}`.trim()} />
              <Field label="Email" value={driver?.email} />
              <Field label="Phone" value={driver?.phone} />
              <Field label="Driver's License #" value={driver?.licenseNumber} />
            </div>
            <Field label="Residential Address" value={residentialLine || "Not set — update your profile"} />
            <Field label="Emergency Contact" value={
              driver?.emergencyContactName
                ? `${driver.emergencyContactName}${driver.emergencyContactPhone ? " — " + driver.emergencyContactPhone : ""}`
                : undefined
            } />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-700">Agreement Details</p>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Effective Date</label>
              <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                <input type="checkbox" checked={sameAsResidential} onChange={(e) => setSameAsResidential(e.target.checked)} className="accent-blue-600 w-4 h-4" />
                <span className="font-medium text-gray-700">Mailing address is the same as residential</span>
              </label>
              {!sameAsResidential && (
                <div className="space-y-2 pl-6">
                  {[["street", "Street Address"], ["city", "City"], ["state", "State"], ["zip", "ZIP Code"]].map(([k, lbl]) => (
                    <div key={k}>
                      <label className="block text-xs text-gray-500 mb-0.5">{lbl}</label>
                      <input value={mailing[k]} onChange={(e) => setMailing({ ...mailing, [k]: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <a href="/documents/independent_contractor_agreement.docx" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:underline font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              Read the full Independent Contractor Agreement ↗
            </a>
            <p className="text-xs text-gray-400 mt-1">You should read the complete agreement before proceeding to sign.</p>
          </div>

          <button onClick={() => setStep(1)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl text-sm transition">
            I Have Reviewed My Information — Continue →
          </button>
        </div>
      )}

      {/* ── Step 1: Policy Acknowledgment Checklist ──────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-900 mb-1">Driver Policy Acknowledgment Checklist</p>
            <p className="text-xs text-gray-500 mb-4">Initial each item below to confirm you have read and agree to each policy section. Your initials will appear in the signed document.</p>

            <div className="space-y-3">
              {CHECKLIST.map(({ key, label, section }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-medium">{label}</p>
                    <p className="text-xs text-gray-400">{section}</p>
                  </div>
                  <input
                    value={checklistInitials[key]}
                    onChange={(e) => setChecklistInitials({ ...checklistInitials, [key]: e.target.value.toUpperCase() })}
                    placeholder="Initials"
                    maxLength={5}
                    className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm text-center font-semibold uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>

          <button onClick={() => setStep(2)} disabled={!allInitialsFilled}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl text-sm transition">
            All Sections Initialed — Proceed to Sign →
          </button>
          {!allInitialsFilled && <p className="text-xs text-center text-gray-400">All 12 items must be initialed to continue.</p>}
        </div>
      )}

      {/* ── Step 2: Sign ─────────────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-800 mb-1">Ready to sign</p>
            <p className="text-xs text-green-700">Effective date: <strong>{new Date(effectiveDate + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong></p>
            <p className="text-xs text-green-700 mt-0.5">All {CHECKLIST.length} policy sections initialed</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Full Legal Name (Printed)</label>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Type your full legal name as it appears on your ID" />
            </div>
            <SignaturePad onSave={handleSign} onCancel={() => setStep(1)} />
            {signing && <p className="text-sm text-center text-gray-500">Generating signed agreement…</p>}
          </div>
        </div>
      )}
    </div>
  );
}
